import * as React from "react";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Button,
  EmptyState,
  Input,
  LastUpdated,
  NavRail,
  PageHeader,
  Table,
  type Column,
} from "@ardept/design-system";
import { useTenants, useMe, type TenantsFilter } from "../api/queries.js";
import type { OperatorTenantDto } from "../api/types.js";
import "./TenantsScreen.css";
import "./screens.css";

export function TenantsScreen(): JSX.Element {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { data: me } = useMe();

  // D-URL-01: URL is source of truth for filter state — survives page refresh
  const filter: TenantsFilter = useMemo(
    () => ({
      q: params.get("q") ?? undefined,
      emptyChannelsOnly: params.get("empty_channels_only") === "true",
      after: params.get("after") ?? undefined,
    }),
    [params],
  );

  const { data, isLoading, error, dataUpdatedAt } = useTenants(filter);

  function setSearch(q: string) {
    const next = new URLSearchParams(params);
    if (q) next.set("q", q);
    else next.delete("q");
    next.delete("after");
    setParams(next, { replace: true });
  }

  function toggleEmptyChannels() {
    const next = new URLSearchParams(params);
    if (next.get("empty_channels_only") === "true") {
      next.delete("empty_channels_only");
    } else {
      next.set("empty_channels_only", "true");
    }
    next.delete("after");
    setParams(next, { replace: true });
  }

  function clearFilters() {
    setParams(new URLSearchParams(), { replace: true });
  }

  function loadMore() {
    if (data?.next_cursor) {
      const next = new URLSearchParams(params);
      next.set("after", data.next_cursor);
      setParams(next, { replace: true });
    }
  }

  // Build operator display for NavRail from /me response
  const operatorDisplay = {
    name: me?.email
      ? (me.email.split("@")[0] ?? me.email)
          .replace(/[._-]/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
      : "Operator",
    email: me?.email ?? "",
  };

  const columns: Column<OperatorTenantDto>[] = [
    {
      key: "slug",
      label: "Slug",
      sortable: true,
      sortValue: (r) => r.slug,
    },
    {
      key: "display_name",
      label: "Display name",
      sortable: true,
      sortValue: (r) => r.display_name,
    },
    {
      key: "channel_count",
      label: "Channels",
      align: "right",
      sortable: true,
      sortValue: (r) => r.channel_count,
      render: (r) => r.channel_count,
    },
    {
      key: "model_id_allowlist",
      label: "Models",
      render: (r) =>
        r.model_id_allowlist.length === 0 ? (
          <span className="muted">—</span>
        ) : (
          <span className="muted">{r.model_id_allowlist.length}</span>
        ),
    },
    {
      key: "operator_count",
      label: "Operators",
      align: "right",
      sortable: true,
      sortValue: (r) => r.operator_count,
      render: (r) => r.operator_count,
    },
    {
      key: "created_at",
      label: "Created",
      sortable: true,
      sortValue: (r) => new Date(r.created_at).getTime(),
      render: (r) => new Date(r.created_at).toLocaleDateString(),
    },
  ];

  const tenants = data?.tenants ?? [];
  const isEmptyAll =
    !isLoading && !error && tenants.length === 0 && !filter.q && !filter.emptyChannelsOnly;
  const isEmptyFiltered =
    !isLoading && !error && tenants.length === 0 && (Boolean(filter.q) || filter.emptyChannelsOnly);

  const emptyState = isEmptyAll ? (
    <EmptyState
      iconName="building-2"
      title="No tenants yet"
      body="Run ardept bootstrap --tenant <slug> to create one."
    />
  ) : isEmptyFiltered ? (
    <div className="empty">
      <EmptyState
        iconName="search-x"
        title="No tenants match these filters"
        body="Try widening the search or clearing filters."
      />
      <div className="empty-clear">
        <Button variant="ghost" onClick={clearFilters}>
          Clear filters
        </Button>
      </div>
    </div>
  ) : null;

  const tableFooter =
    data?.next_cursor ? (
      <div className="tbl-foot">
        <span className="count">
          Showing <b>{tenants.length}</b> tenants
        </span>
        <Button variant="ghost" onClick={loadMore}>
          Load more
        </Button>
      </div>
    ) : tenants.length > 0 ? (
      <div className="tbl-foot">
        <span className="count">
          Showing <b>{tenants.length}</b> tenants
        </span>
      </div>
    ) : null;

  return (
    <div className="app">
      <NavRail active="tenants" operator={operatorDisplay} onNavigate={navigate} />
      <main className="main">
        <PageHeader
          title="Tenants"
          subtitle="Configured tenants and the channels enabled for each. Read-only in v1.3."
          actions={dataUpdatedAt ? <LastUpdated timestamp={dataUpdatedAt} /> : undefined}
        />

        <div className="filters">
          <div className="filters__group">
            <label>Search</label>
            <Input
              variant="search"
              placeholder="Search by slug or display name"
              value={filter.q ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            />
          </div>
          <div className="filters__group tenants-filter-check">
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={filter.emptyChannelsOnly}
                onChange={toggleEmptyChannels}
              />
              <span>Empty channels only</span>
            </label>
          </div>
          <div className="filters__spacer" />
          <Button variant="ghost" onClick={clearFilters}>
            Reset filters
          </Button>
        </div>

        {isLoading && !data ? (
          <div className="list-loading">Loading tenants...</div>
        ) : error ? (
          <div className="list-error">Failed to load tenants. Try again.</div>
        ) : (
          <Table<OperatorTenantDto>
            columns={columns}
            rows={tenants}
            onRowClick={(row) => navigate(`/tenants/${row.id}`)}
            stickyHeader
            emptyState={emptyState}
            footer={tableFooter}
          />
        )}
      </main>
    </div>
  );
}
